import LayerDiffs, { PropertyDiff } from "../model/LayerDiffs";
import {
    TransformedBlur,
    TransformedBorder,
    TransformedFill,
    TransformedLayer,
    TransformedLayerDiffs,
    TransformedLayerRect,
    TransformedLayout,
    TransformedRadius,
    TransformedShadow,
    TransformedTypeface
} from "../model/transform";
import BasicRecord from "./BasicRecord";

// eslint-disable-next-line complexity
function transformDiffs(
    versionDiffs: LayerDiffs,
    prevLayer: TransformedLayer,
    nextLayer: TransformedLayer
): TransformedLayerDiffs {
    const transformedDiffs: TransformedLayerDiffs = {};
    if (versionDiffs.textStyles) {
        const transformedTypefaceDiff: BasicRecord<PropertyDiff<TransformedTypeface>> = {};
        Object.entries(versionDiffs.textStyles).forEach(([index, value]) => {
            const idx = Number(index);
            if (value.add) {
                transformedTypefaceDiff[index] = { add: nextLayer.typefaces[idx] };
            } else if (value.remove) {
                transformedTypefaceDiff[index] = { remove: prevLayer.typefaces[idx] };
            } else if (value.change) {
                transformedTypefaceDiff[index] = {
                    change: {
                        oldValues: prevLayer.typefaces[idx],
                        newValues: nextLayer.typefaces[idx]
                    }
                };
            }
        });
        transformedDiffs.typefaces = transformedTypefaceDiff;
    }

    if (versionDiffs.fills) {
        const transformedFillDiffs: BasicRecord<PropertyDiff<TransformedFill>> = {};
        Object.entries(versionDiffs.fills).forEach(([index, value]) => {
            const idx = Number(index);
            if (value.add) {
                transformedFillDiffs[index] = { add: nextLayer.fills[idx] };
            } else if (value.remove) {
                transformedFillDiffs[index] = { remove: prevLayer.fills[idx] };
            } else if (value.change) {
                transformedFillDiffs[index] = {
                    change: {
                        oldValues: prevLayer.fills[idx],
                        newValues: nextLayer.fills[idx]
                    }
                };
            }
        });
        transformedDiffs.fills = transformedFillDiffs;
    }

    if (versionDiffs.rect) {
        const transformedRectDiff: PropertyDiff<TransformedLayerRect> = {
            change: {
                oldValues: prevLayer.rect,
                newValues: nextLayer.rect
            }
        };
        transformedDiffs.rect = transformedRectDiff;
    }

    if (versionDiffs.opacity) {
        const transformedOpacity: PropertyDiff<string> = {
            change: {
                oldValues: prevLayer.opacity || "100%",
                newValues: nextLayer.opacity || "100%"
            }
        };
        transformedDiffs.opacity = transformedOpacity!;
    }

    if (versionDiffs.borderRadius || versionDiffs.cornerRadius) {
        const transformedBorderRadius: PropertyDiff<TransformedRadius> = {
            change: {
                oldValues: prevLayer.radius! || { borderRadiusTransformed: "0px" },
                newValues: nextLayer.radius! || { borderRadiusTransformed: "0px" }
            }
        };
        transformedDiffs.radius = transformedBorderRadius!;
    }

    if (versionDiffs.rotation) {
        const transformedRotation: PropertyDiff<number> = {
            change: {
                oldValues: prevLayer.rotation! || 0,
                newValues: nextLayer.rotation! || 0
            }
        };
        transformedDiffs.rotation = transformedRotation!;
    }

    if (versionDiffs.blendMode) {
        const transformedBlendMode: PropertyDiff<string> = {
            change: {
                oldValues: prevLayer.blendMode! || "Normal",
                newValues: nextLayer.blendMode! || "Normal"
            }
        };
        transformedDiffs.blendMode = transformedBlendMode!;
    }

    if (versionDiffs.blur) {
        let transformedBlur: PropertyDiff<TransformedBlur>;
        if (versionDiffs.blur.add) {
            transformedBlur = { add: nextLayer.blur };
        } else if (versionDiffs.blur.remove) {
            transformedBlur = { remove: prevLayer.blur };
        } else {
            transformedBlur = { change: {
                oldValues: prevLayer.blur!,
                newValues: nextLayer.blur!
            } };
        }
        transformedDiffs.blur = transformedBlur;
    }

    if (versionDiffs.borders) {
        const transformedBorders: BasicRecord<PropertyDiff<TransformedBorder>> = {};
        Object.entries(versionDiffs.borders).forEach(([index, value]) => {
            const idx = Number(index);
            if (value.add) {
                transformedBorders[index] = { add: nextLayer.borders[idx] };
            } else if (value.remove) {
                transformedBorders[index] = { remove: prevLayer.borders[idx] };
            } else if (value.change) {
                transformedBorders[index] = {
                    change: {
                        oldValues: prevLayer.borders[idx],
                        newValues: nextLayer.borders[idx]
                    }
                };
            }
        });
        transformedDiffs.borders = transformedBorders;
    }

    if (versionDiffs.shadows) {
        const transformedhadows: BasicRecord<PropertyDiff<TransformedShadow>> = {};
        Object.entries(versionDiffs.shadows).forEach(([index, value]) => {
            const idx = Number(index);
            if (value.add) {
                transformedhadows[index] = { add: nextLayer.shadows[idx] };
            } else if (value.remove) {
                transformedhadows[index] = { remove: prevLayer.shadows[idx] };
            } else if (value.change) {
                transformedhadows[index] = {
                    change: {
                        oldValues: prevLayer.shadows[idx],
                        newValues: nextLayer.shadows[idx]
                    }
                };
            }
        });
        transformedDiffs.shadows = transformedhadows;
    }

    if (versionDiffs.content) {
        transformedDiffs.content = versionDiffs.content;
    }

    if (versionDiffs.exportable) {
        transformedDiffs.exportable = versionDiffs.exportable;
    }

    if (versionDiffs.bitmapImageHash) {
        transformedDiffs.bitmapImageHash = versionDiffs.bitmapImageHash;
    }

    if (versionDiffs.layerHashes) {
        transformedDiffs.layerHashes = versionDiffs.layerHashes;
    }

    if (versionDiffs.constraints) {
        transformedDiffs.constraints = versionDiffs.constraints;
    }

    if (versionDiffs.layout) {
        let transformedLayout: PropertyDiff<TransformedLayout>;
        if (versionDiffs.layout.add) {
            transformedLayout = { add: nextLayer.layout };
        } else if (versionDiffs.layout.remove) {
            transformedLayout = { remove: prevLayer.layout };
        } else {
            transformedLayout = { change: {
                oldValues: prevLayer.layout!,
                newValues: nextLayer.layout!
            } };
        }
        transformedDiffs.layout = transformedLayout;
    }

    return transformedDiffs;
}

export default transformDiffs;
